import { createFileRoute, Link } from "../lib/router-compat";
import { PageShell, PanImage } from "../components/PageShell";

export const Route = createFileRoute("/last-night")({
  head: () => ({
    meta: [
      { title: "Last Night! — New Year's Eve in Brooksville" },
      { name: "description", content: "Music, theater, dance — a free New Year's Eve celebration in Downtown South Brooksville, Maine." },
    ],
  }),
  component: LastNightPage,
});

const VENUES = [
  { time: "6:00 – 7:30 pm", name: "South Brooksville Methodist Church" },
  { time: "7:30 – 10:00 pm", name: "Brooksville Community Center" },
];

const ACTS = ["Music", "Dance", "Crafts", "Comedy", "Film", "Poetry", "Fortune"];

function LastNightPage() {
  return (
    <PageShell
      tag=""
      title={<>Last <span className="text-primary">Night!</span> is coming.</>}
      intro="Music · Theater · Dance — 6 to 10 pm in Downtown South Brooksville. South Brooksville Methodist Church (6:00–7:30) and Brooksville Community Center (7:30–10:00)."
    >
      {/* Hero with overlay marquee */}
      <div className="relative overflow-hidden rounded-3xl border border-border/40">
        <div className="aspect-[16/10]">
          <PanImage id="SIGN.jpg" alt="Last Night! sign" width={1800} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 p-8 md:p-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-background/60 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.35em] text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" /> Free · All ages · NYE
          </div>
          <p className="mt-4 font-display italic text-3xl md:text-5xl leading-[1.05] max-w-3xl">
            One night. One downtown. Every kind of stage.
          </p>
        </div>
      </div>

      {/* Quick facts */}
      <dl className="mt-10 grid grid-cols-3 divide-x divide-border/40 rounded-2xl border border-border/40 bg-card/30">
        <div className="px-4 py-6 text-center">
          <dt className="font-display italic text-2xl md:text-4xl text-primary leading-none">6 – 10 pm</dt>
          <dd className="mt-2 text-[10px] uppercase tracking-[0.35em] text-foreground/55">When</dd>
        </div>
        <div className="px-4 py-6 text-center">
          <dt className="font-display italic text-2xl md:text-4xl text-primary leading-none">313 Coastal Rd</dt>
          <dd className="mt-2 text-[10px] uppercase tracking-[0.35em] text-foreground/55">Downtown S. Brooksville</dd>
        </div>
        <div className="px-4 py-6 text-center">
          <dt className="font-display italic text-2xl md:text-4xl text-primary leading-none">Free</dt>
          <dd className="mt-2 text-[10px] uppercase tracking-[0.35em] text-foreground/55">A gift to the community</dd>
        </div>
      </dl>

      {/* Acts marquee */}
      <div className="mt-20">
        <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80 mb-5">What's on</div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 font-display italic text-2xl md:text-4xl leading-tight">
          {ACTS.map((a, i) => (
            <span key={a} className="flex items-baseline gap-x-6">
              <span className={i % 2 === 0 ? "text-foreground" : "text-primary/70"}>{a}</span>
              {i < ACTS.length - 1 && <span className="text-foreground/20" aria-hidden>·</span>}
            </span>
          ))}
        </div>
      </div>

      {/* Schedule by venue */}
      <div className="mt-24">
        <div className="flex items-end justify-between gap-6 mb-10 border-b border-border/40 pb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">The night, room by room</div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl">Two venues, one night</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {VENUES.map((v, i) => (
            <div key={v.name} className="rounded-2xl border border-border/40 bg-card/30 p-7 hover:border-primary/40 transition">
              <div className="flex items-baseline gap-4">
                <span className="font-display italic text-3xl text-primary/40 leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="h-px flex-1 bg-border/50" />
              </div>
              <div className="mt-4 text-[11px] uppercase tracking-[0.35em] text-primary">{v.time}</div>
              <h3 className="mt-2 font-display italic text-2xl">{v.name}</h3>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-foreground/60">
          Performances run roughly 45 minutes. Look for printed schedules around town, or check back here closer to the date.
        </p>
      </div>

      {/* Performer CTA */}
      <div className="mt-24 rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 via-card/40 to-transparent p-8 md:p-12">
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">Want to perform?</div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl leading-[1.05]">
              Send us your act. We'll find you a stage and a slot.
            </h2>
            <p className="mt-4 text-sm text-foreground/65 max-w-xl">
              A brief description is all we need. Every performance is a gift to the community.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-7 py-3 bg-primary text-primary-foreground text-[11px] tracking-[0.3em] uppercase rounded-full hover:opacity-90 transition shadow-lg shadow-primary/20 whitespace-nowrap"
            >
              Submit your act
            </Link>
          </div>
        </div>
        <p className="mt-8 text-sm text-foreground/55 italic">— Nigel and the Last Night Committee</p>
      </div>
    </PageShell>
  );
}